<template>
  <div class="data-project-container">
    <!-- 页面标题区域 -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <div class="title-icon">
            <el-icon><FolderOpened /></el-icon>
          </div>
          <div class="title-text">
            <h1>数据项目</h1>
            <p>管理系统中的数据项目，包括项目创建、编辑和状态管理</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button type="primary" @click="handleCreate" class="action-btn">
            <el-icon><Plus /></el-icon>
            新建项目
          </el-button>
        </div>
      </div>
    </div>

    <el-card class="main-card">
      <!-- 筛选栏 -->
      <div class="filter-bar">
        <div class="filter-header">
          <el-icon><Search /></el-icon>
          <span>筛选条件</span>
        </div>
        <el-form :inline="true" class="filter-form">
          <el-form-item label="项目名称">
            <el-input
              v-model="searchQuery.name"
              placeholder="请输入项目名称"
              style="width: 200px"
              clearable
              @change="handleSearch"
            >
              <template #prefix>
                <el-icon><FolderOpened /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item label="所属科室">
            <el-select
              v-model="searchQuery.sub_department_id"
              placeholder="全部科室"
              style="width: 160px"
              clearable
              @change="handleSearch"
            >
              <el-option label="未选择科室" :value="0" />
              <el-option
                v-for="dept in subDepartments"
                :key="dept.id"
                :label="dept.name"
                :value="dept.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button @click="handleReset" class="reset-btn">
              <el-icon><Refresh /></el-icon>
              重置
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 项目列表 -->
      <div class="table-section">
        <div class="table-header">
          <el-icon><List /></el-icon>
          <span>项目列表</span>
          <span class="table-count">(共 {{ tableData.length }} 个项目)</span>
        </div>
        <el-table
          v-loading="loading"
          :data="tableData"
          border
          stripe
          highlight-current-row
          style="width: 100%"
          class="project-table"
        >
          <el-table-column prop="name" label="项目名称" min-width="150" />
          <el-table-column label="状态" width="100" align="center">
            <template #default="scope">
              <el-tag
                :type="scope.row.status === 'active' ? 'success' : 'danger'"
              >
                {{ scope.row.status === "active" ? "启用" : "停用" }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="类型" width="120" align="center">
            <template #default="scope">
              {{ getProjectType(scope.row) }}
            </template>
          </el-table-column>
          <el-table-column
            prop="created_at"
            label="创建时间"
            align="center"
            width="180"
            :formatter="(row) => formatDateTime(row.created_at)"
          />
          <el-table-column
            prop="updated_at"
            label="更新时间"
            align="center"
            width="180"
            :formatter="(row) => formatDateTime(row.updated_at)"
          />
          <el-table-column label="操作" width="200" align="center">
            <template #default="scope">
              <el-button size="small" @click="handleEdit(scope.row)"
                >编辑</el-button
              >
              <el-button
                size="small"
                :type="scope.row.status === 'active' ? 'danger' : 'success'"
                @click="toggleStatus(scope.row)"
              >
                {{ scope.row.status === "active" ? "停用" : "启用" }}
              </el-button>
              <el-button
                size="small"
                type="danger"
                @click="handleDelete(scope.row)"
                >删除</el-button
              >
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="pagination.currentPage"
          :page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import {
  listDataProjects,
  deleteDataProject,
  toggleDataProjectStatus,
} from "@/api/report-system/dataProject";
import { formatDateTime } from "@/utils/date";
import { getAllSubDepartments } from "@/api/user-management/sub_department";
import {
  FolderOpened,
  Plus,
  Search,
  Refresh,
  List,
} from "@element-plus/icons-vue";

export default {
  name: "DataProject",
  components: {
    FolderOpened,
    Plus,
    Search,
    Refresh,
    List,
  },
  data() {
    return {
      searchQuery: {
        name: "",
        type: "",
        sub_department_id: "",
      },
      subDepartments: [],
      tableData: [],
      loading: false,
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  created() {
    this.fetchData();
    this.fetchSubDepartments();
  },
  methods: {
    formatDateTime(t) {
      return formatDateTime(t);
    },
    async fetchSubDepartments() {
      try {
        const response = await getAllSubDepartments();
        this.subDepartments = response.data || [];
      } catch (error) {
        console.error("获取科室列表失败", error);
      }
    },
    fetchData() {
      this.loading = true;
      const params = {
        page: this.pagination.currentPage,
        size: this.pagination.pageSize,
        ...this.searchQuery,
      };

      listDataProjects(params)
        .then((response) => {
          this.tableData = response.data.rows; // 修改为response.data.data
          this.pagination.total = response.data.total; // 修改为response.data.total
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleCreate() {
      this.$router.push({ name: "ProjectEdit" });
    },
    handleEdit(row) {
      this.$router.push({
        name: "ProjectEdit",
        params: { id: row.id },
      });
    },
    handleDelete(row) {
      this.$confirm("确认删除该项目吗?", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          deleteDataProject(row.id).then(() => {
            this.$message.success("删除成功");
            this.fetchData();
          });
        })
        .catch(() => {
          this.$message.info("已取消删除");
        });
    },
    handleSearch() {
      this.pagination.currentPage = 1;
      this.fetchData();
    },
    handleReset() {
      this.searchQuery = {
        name: "",
        type: "",
        sub_department_id: "",
      };
      this.pagination.currentPage = 1;
      this.fetchData();
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.fetchData();
    },
    handleCurrentChange(val) {
      this.pagination.currentPage = val;
      this.fetchData();
    },
    getProjectType(project) {
      if (project.type === "once") return "单次项目";
      if (project.period === "monthly") return "月报项目";
      if (project.period === "weekly") return "周报项目";
      return "未知类型";
    },
    async toggleStatus(project) {
      try {
        this.loading = true;
        const newStatus = project.status === "active" ? "inactive" : "active";
        await toggleDataProjectStatus(project.id, newStatus);
        // 根据新状态显示不同的成功消息
        if (newStatus === "active") {
          this.$message.success("项目已启用");
        } else {
          this.$message.error("项目已停用");
        }
        this.fetchData(); // 刷新列表数据
      } catch (error) {
        this.$message.error("状态切换失败");
        console.error("状态切换失败:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.main-card {
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* 页面标题区域 */
.page-header {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(255, 255, 255, 0.7) 100%
  );
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 24px 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.title-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.title-icon .el-icon {
  font-size: 28px;
  color: white;
}

.title-text h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.title-text p {
  margin: 4px 0 0 0;
  font-size: 14px;
  color: #7f8c8d;
}

.action-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* 筛选栏样式 */
.filter-bar {
  margin-bottom: 30px;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.filter-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.filter-header .el-icon {
  color: #409eff;
}

.filter-form {
  margin: 0;
}

.filter-form :deep(.el-form-item__label) {
  font-weight: 500;
  color: #2c3e50;
}

.search-btn,
.reset-btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.search-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.reset-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

/* 表格样式 */
.table-section {
  margin-bottom: 20px;
}

.table-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.table-header .el-icon {
  color: #409eff;
}

.table-count {
  color: #7f8c8d;
  font-weight: 400;
  font-size: 14px;
}

.project-table {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.project-table :deep(.el-table__row:hover) {
  background-color: #f8f9fa;
}

/* 分页样式 */
.pagination-container {
  margin-top: 30px;
  text-align: right;
  padding: 20px 0;
}

.pagination-container :deep(.el-pagination) {
  justify-content: flex-end;
}

.pagination-container :deep(.el-pagination .el-pager li) {
  border-radius: 6px;
  margin: 0 2px;
}

.pagination-container :deep(.el-pagination .el-pager li.is-active) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    padding: 16px 20px;
  }

  .header-content {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .title-text h1 {
    font-size: 24px;
  }

  .filter-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .filter-form :deep(.el-form-item) {
    margin-bottom: 0;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
